/**
 * 🌠 InnerLightUpgradeCard — the owner's one-tap "spend crystals →
 *     +Inner Light" widget for the hero page.
 *
 * Owner directive (Feb 2026):
 *   • Any number of crystals may be entered, up to what is available.
 *   • Cost curve: 10 · 50 · 100 · 200 · 400 · … (doubling after Lv 2).
 *   • Each Inner Light level grants +2% to Mother Phoenix Crystal
 *     contribution, capped at +200%.
 *
 * The widget greedily buys as many whole levels as the budget covers;
 * leftover crystals (less than the next-level cost) stay in the wallet.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmt(n) {
  const v = Number(n || 0);
  return Number.isFinite(v) ? v.toLocaleString() : "0";
}

export default function InnerLightUpgradeCard({ onUpgraded }) {
  const [state, setState]       = useState(null);     // from /inner-light/state
  const [table, setTable]       = useState(null);     // from /inner-light/cost-table
  const [spend, setSpend]       = useState("");
  const [busy, setBusy]         = useState(false);
  const [loading, setLoading]   = useState(true);

  async function refresh() {
    try {
      const [s, t] = await Promise.all([
        axios.get(`${API}/inner-light/state`,       { withCredentials: true }),
        axios.get(`${API}/inner-light/cost-table`,  { withCredentials: true }),
      ]);
      setState(s.data);
      setTable(t.data);
    } catch (e) {
      // Pet-level lock or auth — hide the widget quietly.
      setState({ unlocked: false });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, []);

  if (loading) return null;
  if (!state?.unlocked) {
    return (
      <div
        className="rounded-lg border border-neutral-800 bg-black/40 p-3 text-[11px] text-neutral-500"
        data-testid="inner-light-locked"
      >
        🌠 Inner Light unlocks at Pet Level {state?.unlocks_at_pet_level ?? 10}.
      </div>
    );
  }

  const level     = Number(state.level || 0);
  const crystals  = Number(state.crystals || 0);
  const maxLevel  = Number(table?.max_level || 100);
  const atCeiling = level >= maxLevel;
  const nextCost  = Number((table?.upcoming?.[0]?.cost) || 0);
  const bonusPct  = Math.min(level * 2, 200);
  const canBuyNext = !atCeiling && nextCost > 0 && crystals >= nextCost;

  async function doUpgrade(amount) {
    if (busy) return;
    const want = Math.max(0, Math.floor(Number(amount || 0)));
    if (want <= 0) {
      toast.error("Enter how many crystals you want to spend.");
      return;
    }
    if (want > crystals) {
      toast.error(`You only have ${fmt(crystals)} Inner Light crystals.`);
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(
        `${API}/inner-light/upgrade`,
        { spend: want },
        { withCredentials: true },
      );
      const d = r.data || {};
      toast.success(
        `🌠 +${d.levels_gained} Inner Light · spent ${fmt(d.crystals_spent)} crystals · now Lv ${d.after?.level} (+${d.bonus_pct_now}% MPC)`,
      );
      setSpend("");
      await refresh();
      onUpgraded?.(d);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not level up Inner Light.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="rounded-lg border border-amber-700/40 bg-gradient-to-b from-[#1a1208] to-[#0F0A08] p-3"
      data-testid="inner-light-card"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="text-[9px] uppercase tracking-widest text-amber-300/70">
            Inner Light × Mother Phoenix Crystals
          </div>
          <div className="font-display text-lg font-bold text-amber-100 leading-tight">
            🌠 Inner Light · Lv <span data-testid="inner-light-level">{level}</span>
          </div>
          <div className="text-[10px] text-neutral-400">
            Multiplier now: <span className="text-amber-200 font-bold" data-testid="inner-light-bonus">+{bonusPct}%</span>
            {" "}to every equipped Mother Phoenix Crystal
            {atCeiling && <span className="text-neutral-500"> · (ceiling)</span>}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[9px] uppercase tracking-widest text-neutral-500">Wallet</div>
          <div className="font-mono text-lg font-bold text-amber-200 tabular-nums" data-testid="inner-light-wallet">
            {fmt(crystals)}
          </div>
          <div className="text-[9px] text-neutral-500">crystals</div>
        </div>
      </div>

      {!atCeiling && (
        <>
          <div className="flex items-end gap-2 mb-2">
            <div className="flex-1">
              <label className="block text-[9px] uppercase tracking-widest text-neutral-500 mb-0.5">
                Spend crystals
              </label>
              <input
                type="number"
                inputMode="numeric"
                min={1}
                max={crystals}
                value={spend}
                onChange={(e) => setSpend(e.target.value)}
                placeholder={`1 – ${fmt(crystals)}`}
                disabled={busy || crystals <= 0}
                data-testid="inner-light-spend-input"
                className="w-full bg-black/50 border border-amber-800/50 text-amber-100 placeholder:text-neutral-600 px-2 py-1.5 text-sm font-mono focus:outline-none focus:border-amber-500 disabled:opacity-50"
              />
            </div>
            <button
              onClick={() => doUpgrade(spend)}
              disabled={busy || !spend || Number(spend) <= 0 || crystals <= 0}
              data-testid="inner-light-spend-btn"
              className="bg-amber-500 hover:bg-amber-400 disabled:bg-neutral-800 disabled:text-neutral-600 text-black font-bold uppercase tracking-widest text-[11px] px-3 py-1.5 transition"
            >
              {busy ? "…" : "Spend"}
            </button>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => doUpgrade(nextCost)}
              disabled={busy || !canBuyNext}
              data-testid="inner-light-buy-next-btn"
              className="bg-[#7c3aed] hover:bg-[#8b5cf6] disabled:bg-neutral-800 disabled:text-neutral-600 text-white font-bold text-[11px] uppercase tracking-widest px-2.5 py-1 transition"
            >
              +1 Lv ({fmt(nextCost)})
            </button>
            <button
              onClick={() => doUpgrade(crystals)}
              disabled={busy || crystals < nextCost}
              data-testid="inner-light-spend-all-btn"
              className="bg-neutral-800 hover:bg-neutral-700 disabled:bg-neutral-900 disabled:text-neutral-600 text-amber-200 font-bold text-[11px] uppercase tracking-widest px-2.5 py-1 transition"
              title="Pour everything available into Inner Light — whole levels only"
            >
              Spend All ({fmt(crystals)})
            </button>
            <span className="text-[10px] text-neutral-500">
              Next level: {fmt(nextCost)} crystals
            </span>
          </div>
        </>
      )}

      {atCeiling && (
        <div className="text-[11px] text-amber-200 italic">
          Inner Light is at its ceiling (+200% to Mother Phoenix Crystals).
        </div>
      )}

      {/* Upcoming costs preview */}
      <details className="mt-2 group" data-testid="inner-light-cost-preview">
        <summary className="cursor-pointer text-[10px] text-neutral-500 hover:text-amber-200 uppercase tracking-widest">
          See next 5 upgrade costs
        </summary>
        <ul className="mt-1 text-[10px] space-y-0.5">
          {(table?.upcoming || []).slice(0, 5).map((r) => (
            <li key={r.target_level} className="flex justify-between text-neutral-400">
              <span>Lv {r.target_level}</span>
              <span className="font-mono tabular-nums text-amber-300/80">
                {fmt(r.cost)} crystals → +{r.bonus_pct}%
              </span>
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}
